import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export const supportedLanguages = [
  ["en", "English"],
  ["tr", "Türkçe"],
  ["de", "Deutsch"],
  ["fr", "Français"],
  ["it", "Italiano"],
  ["es", "Español"],
  ["pt", "Português"],
  ["nl", "Nederlands"],
  ["pl", "Polski"],
  ["cs", "Čeština"],
  ["hu", "Magyar"],
  ["ro", "Română"],
  ["el", "Ελληνικά"],
  ["sv", "Svenska"],
  ["da", "Dansk"],
  ["fi", "Suomi"],
  ["no", "Norsk"],
  ["is", "Íslenska"],
  ["sk", "Slovenčina"],
  ["sl", "Slovenščina"],
  ["hr", "Hrvatski"],
  ["bg", "Български"],
  ["et", "Eesti"],
  ["lv", "Latviešu"],
  ["lt", "Lietuvių"],
  ["ga", "Gaeilge"],
  ["mt", "Malti"],
  ["uk", "Українська"],
  ["sr", "Српски"],
  ["sq", "Shqip"],
  ["mk", "Македонски"],
  ["bs", "Bosanski"],
  ["ru", "Русский"],
  ["be", "Беларуская"],
  ["lb", "Lëtzebuergesch"],
  ["ka", "ქართული"],
  ["hy", "Հայերեն"],
  ["ja", "日本語"],
  ["zh", "简体中文"],
  ["ko", "한국어"],
  ["ar", "العربية"],
  ["he", "עברית"],
  ["id", "Bahasa Indonesia"],
  ["ms", "Bahasa Melayu"],
  ["th", "ไทย"],
  ["az", "Azərbaycanca"],
] as const;

export type LanguageCode = (typeof supportedLanguages)[number][0];
const languageFlagByCode: Record<string, string> = {
  en: "🇬🇧",
  tr: "🇹🇷",
  de: "🇩🇪",
  fr: "🇫🇷",
  it: "🇮🇹",
  es: "🇪🇸",
  pt: "🇵🇹",
  nl: "🇳🇱",
  pl: "🇵🇱",
  cs: "🇨🇿",
  hu: "🇭🇺",
  ro: "🇷🇴",
  el: "🇬🇷",
  sv: "🇸🇪",
  da: "🇩🇰",
  fi: "🇫🇮",
  no: "🇳🇴",
  is: "🇮🇸",
  sk: "🇸🇰",
  sl: "🇸🇮",
  hr: "🇭🇷",
  bg: "🇧🇬",
  et: "🇪🇪",
  lv: "🇱🇻",
  lt: "🇱🇹",
  ga: "🇮🇪",
  mt: "🇲🇹",
  uk: "🇺🇦",
  sr: "🇷🇸",
  sq: "🇦🇱",
  mk: "🇲🇰",
  bs: "🇧🇦",
  ru: "🇷🇺",
  be: "🇧🇾",
  lb: "🇱🇺",
  ka: "🇬🇪",
  hy: "🇦🇲",
  ja: "🇯🇵",
  zh: "🇨🇳",
  ko: "🇰🇷",
  ar: "🇸🇦",
  he: "🇮🇱",
  id: "🇮🇩",
  ms: "🇲🇾",
  th: "🇹🇭",
  az: "🇦🇿",
};
export const languageOptions = supportedLanguages.map(([code, name]) => ({
  code,
  name,
  flag: languageFlagByCode[code] ?? "🌐",
}));

const english = {
  "nav.weekend": "Weekend",
  "nav.standings": "Standings",
  "nav.drivers": "Drivers",
  "nav.feedback": "Feedback",
  "nav.about": "About",
  "language.select": "Language",
  "action.close": "Close details",
  "action.expand": "Expand details",
  "action.collapse": "Collapse details",
  "action.sendFeedback": "Send feedback",
  "action.feedbackSent": "Feedback sent",
  "action.sending": "Sending…",
  "action.tryAgain": "Try again",
  "map.displayOptions": "Map display options",
  "map.display": "Map display",
  "map.countryLabels": "Country labels",
  "map.circuits": "Circuits",
  "time.yourTime": "Your time",
  "time.track": "Track",
  "time.live": "Live",
  "time.ongoing": "Ongoing",
  "time.next": "Next",
  "session.scheduled": "Scheduled",
  "session.completed": "Completed",
  "session.cancelled": "Cancelled",
  "session.delayed": "Delayed",
  "weekend.nextSession": "Next session",
  "status.liveSignalUnavailable": "Live signal not connected",
  "status.noSnapshot": "No snapshot source has published yet",
  "status.past": "Past race",
  "status.current": "Current race",
  "status.upcoming": "Upcoming race",
  "circuit.thisWeekend": "This weekend",
  "circuit.sessionResults": "Session results",
  "circuit.publishedResults": "Published results",
  "circuit.raceResult": "Race result",
  "circuit.season": "Season",
  "circuit.winner": "Winner",
  "circuit.points": "points",
  "circuit.noPastResult":
    "This past race has no published result in the current snapshot. Nothing is inferred.",
  "circuit.resultsWillAppear":
    "Results will appear here when this circuit has a completed, published race.",
  "circuit.constructorNotPublished": "Constructor not published",
  "confidence.high": "High confidence",
  "confidence.medium": "Medium confidence",
  "confidence.low": "Unconfirmed",
  "confidence.unknown": "Unknown",
  "standings.back": "Championship",
  "standings.progression": "Season progression",
  "standings.progressionDescription":
    "Published points and position after each completed round.",
  "standings.notPublished": "Progression not published",
  "standings.notPublishedBody":
    "Race-by-race history will appear after the next complete championship refresh.",
  "standings.latest": "Latest published table",
  "standings.title": "Championship",
  "standings.table": "Standings table",
  "standings.drivers": "Drivers",
  "standings.teams": "Teams",
  "standings.racePosition": "Race",
  "standings.afterRound": "after round",
  "driver.search": "Search drivers",
  "driver.noEstimate": "No reliable current estimate",
  "driver.activeGrid": "Still on the active grid",
  "driver.whyHere": "Why we place them here",
  "driver.expectedNext": "Expected next · scheduled",
  "info.evidence": "Evidence attached",
  "info.evidenceBody":
    "Publisher, age, rationale, and confidence band stay visible.",
  "info.unknown": "Unknown is useful",
  "info.unknownBody": "Conflicted or weak information never receives a marker.",
  "info.snapshot": "Snapshot",
  "info.sources": "Sources",
  "info.healthy": "healthy",
  "info.material": "Material",
  "map.reset": "Reset globe view",
  "a11y.driverRoster": "Accessible driver roster",
  "a11y.activeRoster": "Active driver roster",
  "a11y.signalDetails": "Signal details",
  "brand.reset": "Close details and reset Paddock Signal",
  "weekend.noActive": "No active weekend",
  "weekend.noActiveBody":
    "The next published calendar update will appear here.",
  "drivers.fullGrid": "Full active grid",
  "drivers.find": "Find a driver",
  "drivers.searchDescription":
    "Search and filters update both this roster and the globe.",
  "drivers.searchPlaceholder": "Name, acronym, or team",
  "drivers.favorites": "Favorites",
  "drivers.team": "Team",
  "drivers.signal": "Signal",
  "drivers.allTeams": "All teams",
  "drivers.allSignals": "All signals",
  "feedback.eyebrow": "Help tune the signal",
  "feedback.heading": "Feedback",
  "feedback.description":
    "Describe an issue, possible error, or idea. Your message goes directly to the project owner.",
  "feedback.category": "Category",
  "feedback.whatChanged": "What happened or should change?",
  "feedback.includeDiagnostics":
    "Include the reviewed device, browser, viewport, snapshot, preference, and app-error summary below.",
  "feedback.issue": "Usability issue",
  "feedback.error": "Possible data or app error",
  "feedback.idea": "Idea",
  "feedback.placeholder":
    "What did you see, what did you expect, and how can it be reproduced?",
  "feedback.errorMessage":
    "Feedback could not be sent right now. Please try again in a moment.",
  "about.eyebrow": "Read the signal",
  "about.heading": "About these estimates",
  "about.description":
    "Paddock Signal is an independent editorial atlas, not a live-person tracker.",
  "common.noMatchingDrivers": "No matching drivers",
  "common.clearFilters":
    "Clear or broaden the shared filters to restore map signals.",
  "common.noReliableEstimate": "No reliable current estimate",
  "common.noReliableEstimateBody":
    "We keep every active driver visible without placing a marker when evidence is weak or conflicted.",
} as const;

export type TranslationKey = keyof typeof english;
export type Translate = (
  key: TranslationKey,
  values?: Record<string, string | number>,
) => string;

const translated: Partial<
  Record<LanguageCode, Partial<Record<TranslationKey, string>>>
> = {
  tr: {
    "nav.weekend": "Hafta sonu",
    "nav.standings": "Sıralama",
    "nav.drivers": "Pilotlar",
    "nav.feedback": "Geri bildirim",
    "nav.about": "Hakkında",
    "language.select": "Dil",
    "action.close": "Detayları kapat",
    "action.expand": "Detayları genişlet",
    "action.collapse": "Detayları daralt",
    "action.sendFeedback": "Geri bildirim gönder",
    "action.feedbackSent": "Geri bildirim gönderildi",
    "action.sending": "Gönderiliyor…",
    "action.tryAgain": "Tekrar dene",
    "map.displayOptions": "Harita görüntü seçenekleri",
    "map.display": "Harita görüntüsü",
    "map.countryLabels": "Ülke etiketleri",
    "map.circuits": "Pistler",
    "time.yourTime": "Sizin saatiniz",
    "time.track": "Pist",
    "session.scheduled": "Planlandı",
    "session.completed": "Tamamlandı",
    "session.cancelled": "İptal edildi",
    "session.delayed": "Gecikmeli",
    "time.live": "Canlı",
    "time.ongoing": "Devam ediyor",
    "time.next": "Sıradaki",
    "weekend.nextSession": "Sıradaki seans",
    "status.liveSignalUnavailable": "Canlı sinyal bağlı değil",
    "status.noSnapshot": "Henüz snapshot kaynağı yayın yapmadı",
    "status.past": "Geçmiş yarış",
    "status.current": "Güncel yarış",
    "status.upcoming": "Yaklaşan yarış",
    "circuit.thisWeekend": "Bu hafta sonu",
    "circuit.sessionResults": "Seans sonuçları",
    "circuit.publishedResults": "Yayınlanmış sonuçlar",
    "circuit.raceResult": "Yarış sonucu",
    "circuit.season": "Sezon",
    "circuit.winner": "Kazanan",
    "circuit.points": "puan",
    "circuit.noPastResult":
      "Bu geçmiş yarışın mevcut snapshot içinde yayınlanmış sonucu yok. Hiçbir bilgi çıkarılmadı.",
    "circuit.resultsWillAppear":
      "Bu pistte tamamlanmış ve yayınlanmış bir yarış olduğunda sonuçlar burada görünür.",
    "circuit.constructorNotPublished": "Konstrukta ait veri yayınlanmadı",
    "confidence.high": "Yüksek güven",
    "confidence.medium": "Orta güven",
    "confidence.low": "Doğrulanmadı",
    "confidence.unknown": "Bilinmiyor",
    "standings.back": "Şampiyona",
    "standings.progression": "Sezon ilerlemesi",
    "standings.progressionDescription":
      "Tamamlanan her turdan sonraki yayınlanmış puan ve konum.",
    "standings.notPublished": "İlerleme yayınlanmadı",
    "standings.notPublishedBody":
      "Yarış yarış geçmiş, bir sonraki tam şampiyona yenilemesinden sonra görünür.",
    "standings.latest": "Son yayınlanan tablo",
    "standings.title": "Şampiyona",
    "standings.table": "Sıralama tablosu",
    "standings.drivers": "Pilotlar",
    "standings.teams": "Takımlar",
    "standings.racePosition": "Yarış",
    "standings.afterRound": "tur sonrası",
    "driver.search": "Pilotları ara",
    "driver.noEstimate": "Güvenilir güncel tahmin yok",
    "driver.activeGrid": "Aktif gridde kalıyor",
    "driver.whyHere": "Neden burada gösteriliyor",
    "driver.expectedNext": "Beklenen sonraki · planlandı",
    "info.evidence": "Kanıt eklendi",
    "info.evidenceBody":
      "Yayıncı, yaş, gerekçe ve güven seviyesi görünür kalır.",
    "info.unknown": "Bilinmeyen değerlidir",
    "info.unknownBody": "Çelişkili veya zayıf bilgiler asla işaretçi almaz.",
    "info.snapshot": "Snapshot",
    "info.sources": "Kaynaklar",
    "info.healthy": "sağlıklı",
    "info.material": "Materyal",
    "map.reset": "Küre görünümünü sıfırla",
    "a11y.driverRoster": "Erişilebilir pilot listesi",
    "a11y.activeRoster": "Aktif pilot listesi",
    "a11y.signalDetails": "Sinyal detayları",
    "brand.reset": "Detayları kapat ve Paddock Signal'i sıfırla",
    "weekend.noActive": "Aktif hafta sonu yok",
    "weekend.noActiveBody":
      "Bir sonraki yayınlanmış takvim güncellemesi burada görünecek.",
    "drivers.fullGrid": "Aktif grid",
    "drivers.find": "Pilot bul",
    "drivers.searchDescription":
      "Arama ve filtreler hem listeyi hem küreyi günceller.",
    "drivers.searchPlaceholder": "İsim, kısaltma veya takım",
    "drivers.favorites": "Favoriler",
    "drivers.team": "Takım",
    "drivers.signal": "Sinyal",
    "drivers.allTeams": "Tüm takımlar",
    "drivers.allSignals": "Tüm sinyaller",
    "feedback.eyebrow": "Sinyali birlikte geliştirelim",
    "feedback.heading": "Geri bildirim",
    "feedback.description":
      "Bir sorun, olası hata veya fikir paylaşın. Mesajınız doğrudan proje sahibine iletilir.",
    "feedback.category": "Kategori",
    "feedback.whatChanged": "Ne oldu veya ne değişmeli?",
    "feedback.includeDiagnostics":
      "İncelenen cihaz, tarayıcı, ekran, snapshot, tercih ve uygulama hata özetini ekle.",
    "feedback.issue": "Kullanılabilirlik sorunu",
    "feedback.error": "Olası veri veya uygulama hatası",
    "feedback.idea": "Fikir",
    "feedback.placeholder":
      "Ne gördünüz, ne bekliyordunuz ve nasıl tekrarlanabilir?",
    "feedback.errorMessage":
      "Geri bildirim şu anda gönderilemedi. Biraz sonra tekrar deneyin.",
    "about.eyebrow": "Sinyali okuyun",
    "about.heading": "Bu tahminler hakkında",
    "about.description":
      "Paddock Signal bağımsız bir editoryal atlas; canlı kişi takipçisi değildir.",
    "common.noMatchingDrivers": "Eşleşen pilot yok",
    "common.clearFilters":
      "Harita sinyallerini geri getirmek için filtreleri temizleyin veya genişletin.",
    "common.noReliableEstimate": "Güvenilir güncel tahmin yok",
    "common.noReliableEstimateBody":
      "Kanıt zayıf veya çelişkili olduğunda aktif pilotları görünür tutar, ancak işaretçi koymayız.",
  },
  de: {
    "nav.weekend": "Wochenende",
    "nav.standings": "Rangliste",
    "nav.drivers": "Fahrer",
    "nav.feedback": "Feedback",
    "nav.about": "Über uns",
    "language.select": "Sprache",
    "action.close": "Details schließen",
    "action.expand": "Details erweitern",
    "action.collapse": "Details einklappen",
    "action.sendFeedback": "Feedback senden",
    "action.feedbackSent": "Feedback gesendet",
    "action.sending": "Wird gesendet…",
    "map.displayOptions": "Kartenoptionen",
    "map.countryLabels": "Ländernamen",
    "map.circuits": "Strecken",
    "time.yourTime": "Deine Zeit",
    "time.track": "Strecke",
    "status.past": "Vergangenes Rennen",
    "status.current": "Aktuelles Rennen",
    "status.upcoming": "Kommendes Rennen",
    "circuit.raceResult": "Rennergebnis",
    "circuit.season": "Saison",
    "circuit.winner": "Sieger",
    "circuit.points": "Punkte",
  },
  fr: {
    "nav.weekend": "Week-end",
    "nav.standings": "Classement",
    "nav.drivers": "Pilotes",
    "nav.feedback": "Retour",
    "nav.about": "À propos",
    "language.select": "Langue",
    "action.close": "Fermer les détails",
    "action.expand": "Développer les détails",
    "action.collapse": "Réduire les détails",
    "action.sendFeedback": "Envoyer",
    "action.feedbackSent": "Envoyé",
    "action.sending": "Envoi…",
    "map.displayOptions": "Options de carte",
    "map.countryLabels": "Noms des pays",
    "map.circuits": "Circuits",
    "time.yourTime": "Votre heure",
    "time.track": "Circuit",
    "status.past": "Course passée",
    "status.current": "Course actuelle",
    "status.upcoming": "Course à venir",
    "circuit.raceResult": "Résultat de la course",
    "circuit.season": "Saison",
    "circuit.winner": "Vainqueur",
    "circuit.points": "points",
  },
  it: {
    "nav.weekend": "Weekend",
    "nav.standings": "Classifica",
    "nav.drivers": "Piloti",
    "nav.feedback": "Feedback",
    "nav.about": "Info",
    "language.select": "Lingua",
    "action.close": "Chiudi dettagli",
    "action.expand": "Espandi dettagli",
    "action.collapse": "Riduci dettagli",
    "action.sendFeedback": "Invia feedback",
    "action.feedbackSent": "Feedback inviato",
    "action.sending": "Invio…",
    "map.displayOptions": "Opzioni mappa",
    "map.countryLabels": "Nomi dei paesi",
    "map.circuits": "Circuiti",
    "time.yourTime": "La tua ora",
    "time.track": "Pista",
    "status.past": "Gara passata",
    "status.current": "Gara attuale",
    "status.upcoming": "Gara imminente",
    "circuit.raceResult": "Risultato di gara",
    "circuit.season": "Stagione",
    "circuit.winner": "Vincitore",
    "circuit.points": "punti",
  },
  es: {
    "nav.weekend": "Fin de semana",
    "nav.standings": "Clasificación",
    "nav.drivers": "Pilotos",
    "nav.feedback": "Comentarios",
    "nav.about": "Acerca de",
    "language.select": "Idioma",
    "action.close": "Cerrar detalles",
    "action.expand": "Ampliar detalles",
    "action.collapse": "Contraer detalles",
    "action.sendFeedback": "Enviar comentarios",
    "action.feedbackSent": "Comentarios enviados",
    "action.sending": "Enviando…",
    "map.displayOptions": "Opciones del mapa",
    "map.countryLabels": "Nombres de países",
    "map.circuits": "Circuitos",
    "time.yourTime": "Tu hora",
    "time.track": "Circuito",
    "status.past": "Carrera pasada",
    "status.current": "Carrera actual",
    "status.upcoming": "Próxima carrera",
    "circuit.raceResult": "Resultado de carrera",
    "circuit.season": "Temporada",
    "circuit.winner": "Ganador",
    "circuit.points": "puntos",
  },
  pt: {
    "nav.weekend": "Fim de semana",
    "nav.standings": "Classificação",
    "nav.drivers": "Pilotos",
    "nav.feedback": "Feedback",
    "nav.about": "Sobre",
    "language.select": "Idioma",
    "action.close": "Fechar detalhes",
    "action.expand": "Expandir detalhes",
    "action.collapse": "Recolher detalhes",
    "action.sendFeedback": "Enviar feedback",
    "action.feedbackSent": "Feedback enviado",
    "action.sending": "A enviar…",
    "map.displayOptions": "Opções do mapa",
    "map.countryLabels": "Nomes dos países",
    "map.circuits": "Circuitos",
    "time.yourTime": "A sua hora",
    "time.track": "Pista",
    "status.past": "Corrida passada",
    "status.current": "Corrida atual",
    "status.upcoming": "Próxima corrida",
    "circuit.raceResult": "Resultado da corrida",
    "circuit.season": "Época",
    "circuit.winner": "Vencedor",
    "circuit.points": "pontos",
  },
  nl: {
    "nav.weekend": "Weekend",
    "nav.standings": "Stand",
    "nav.drivers": "Coureurs",
    "nav.feedback": "Feedback",
    "nav.about": "Over",
    "language.select": "Taal",
    "action.close": "Details sluiten",
    "action.expand": "Details uitklappen",
    "action.collapse": "Details inklappen",
    "action.sendFeedback": "Feedback sturen",
    "action.feedbackSent": "Feedback verzonden",
    "action.sending": "Verzenden…",
    "map.displayOptions": "Kaartopties",
    "map.countryLabels": "Landnamen",
    "map.circuits": "Circuits",
    "time.yourTime": "Jouw tijd",
    "time.track": "Circuit",
    "status.past": "Afgelopen race",
    "status.current": "Huidige race",
    "status.upcoming": "Aankomende race",
    "circuit.raceResult": "Race-uitslag",
    "circuit.season": "Seizoen",
    "circuit.winner": "Winnaar",
    "circuit.points": "punten",
  },
  ja: {
    "nav.weekend": "週末",
    "nav.standings": "ランキング",
    "nav.drivers": "ドライバー",
    "nav.feedback": "フィードバック",
    "nav.about": "概要",
    "language.select": "言語",
    "action.close": "詳細を閉じる",
    "action.expand": "詳細を開く",
    "action.collapse": "詳細を縮小",
    "action.sendFeedback": "送信",
    "action.feedbackSent": "送信しました",
    "action.sending": "送信中…",
    "map.displayOptions": "地図表示",
    "map.countryLabels": "国名",
    "map.circuits": "赛道",
    "time.yourTime": "あなたの時間",
    "time.track": "サーキット",
    "status.past": "終了したレース",
    "status.current": "開催中のレース",
    "status.upcoming": "次のレース",
    "circuit.raceResult": "レース結果",
    "circuit.season": "シーズン",
    "circuit.winner": "優勝",
    "circuit.points": "ポイント",
  },
  zh: {
    "nav.weekend": "周末",
    "nav.standings": "积分榜",
    "nav.drivers": "车手",
    "nav.feedback": "反馈",
    "nav.about": "关于",
    "language.select": "语言",
    "action.close": "关闭详情",
    "action.expand": "展开详情",
    "action.collapse": "收起详情",
    "action.sendFeedback": "发送反馈",
    "action.feedbackSent": "反馈已发送",
    "action.sending": "发送中…",
    "map.displayOptions": "地图显示选项",
    "map.countryLabels": "国家名称",
    "map.circuits": "서킷",
    "time.yourTime": "你的时间",
    "time.track": "赛道",
    "status.past": "已结束比赛",
    "status.current": "当前比赛",
    "status.upcoming": "即将举行",
    "circuit.raceResult": "比赛结果",
    "circuit.season": "赛季",
    "circuit.winner": "冠军",
    "circuit.points": "积分",
  },
  ar: {
    "nav.weekend": "عطلة السباق",
    "nav.standings": "الترتيب",
    "nav.drivers": "السائقون",
    "nav.feedback": "ملاحظات",
    "nav.about": "حول",
    "language.select": "اللغة",
    "action.close": "إغلاق التفاصيل",
    "action.expand": "توسيع التفاصيل",
    "action.collapse": "طي التفاصيل",
    "action.sendFeedback": "إرسال الملاحظات",
    "action.feedbackSent": "تم إرسال الملاحظات",
    "action.sending": "جارٍ الإرسال…",
    "map.displayOptions": "خيارات الخريطة",
    "map.countryLabels": "أسماء الدول",
    "map.circuits": "الحلبات",
    "time.yourTime": "وقتك",
    "time.track": "الحلبة",
    "status.past": "سباق سابق",
    "status.current": "السباق الحالي",
    "status.upcoming": "السباق القادم",
    "circuit.raceResult": "نتيجة السباق",
    "circuit.season": "الموسم",
    "circuit.winner": "الفائز",
    "circuit.points": "نقاط",
  },
  th: {
    "nav.weekend": "สุดสัปดาห์",
    "nav.standings": "อันดับ",
    "nav.drivers": "นักแข่ง",
    "nav.feedback": "ความคิดเห็น",
    "nav.about": "เกี่ยวกับ",
    "language.select": "ภาษา",
    "action.close": "ปิดรายละเอียด",
    "action.expand": "ขยายรายละเอียด",
    "action.collapse": "ย่อรายละเอียด",
    "action.sendFeedback": "ส่งความคิดเห็น",
    "action.feedbackSent": "ส่งความคิดเห็นแล้ว",
    "action.sending": "กำลังส่ง…",
    "map.displayOptions": "ตัวเลือกแผนที่",
    "map.countryLabels": "ชื่อประเทศ",
    "map.circuits": "สนามแข่ง",
    "time.yourTime": "เวลาของคุณ",
    "time.track": "สนามแข่ง",
    "status.past": "การแข่งขันที่ผ่านมา",
    "status.current": "การแข่งขันปัจจุบัน",
    "status.upcoming": "การแข่งขันถัดไป",
    "circuit.raceResult": "ผลการแข่งขัน",
    "circuit.season": "ฤดูกาล",
    "circuit.winner": "ผู้ชนะ",
    "circuit.points": "คะแนน",
  },
  az: {
    "nav.weekend": "Həftəsonu",
    "nav.standings": "Sıralama",
    "nav.drivers": "Pilotlar",
    "nav.feedback": "Rəy",
    "nav.about": "Haqqında",
    "language.select": "Dil",
    "action.close": "Detalları bağla",
    "action.expand": "Detalları aç",
    "action.collapse": "Detalları yığ",
    "action.sendFeedback": "Rəy göndər",
    "action.feedbackSent": "Rəy göndərildi",
    "action.sending": "Göndərilir…",
    "map.displayOptions": "Xəritə seçimləri",
    "map.countryLabels": "Ölkə adları",
    "map.circuits": "Pistler",
    "time.yourTime": "Sizin vaxtınız",
    "time.track": "Tras",
    "status.past": "Keçmiş yarış",
    "status.current": "Cari yarış",
    "status.upcoming": "Gələcək yarış",
    "circuit.raceResult": "Yarış nəticəsi",
    "circuit.season": "Mövsüm",
    "circuit.winner": "Qalib",
    "circuit.points": "xal",
  },
  pl: {
    "nav.weekend": "Weekend",
    "nav.standings": "Klasyfikacja",
    "nav.drivers": "Kierowcy",
    "nav.feedback": "Opinie",
    "nav.about": "O nas",
  },
  cs: {
    "nav.weekend": "Víkend",
    "nav.standings": "Pořadí",
    "nav.drivers": "Jezdci",
    "nav.feedback": "Zpětná vazba",
    "nav.about": "O aplikaci",
  },
  hu: {
    "nav.weekend": "Hétvége",
    "nav.standings": "Tabella",
    "nav.drivers": "Pilóták",
    "nav.feedback": "Visszajelzés",
    "nav.about": "Névjegy",
  },
  ro: {
    "nav.weekend": "Weekend",
    "nav.standings": "Clasament",
    "nav.drivers": "Piloți",
    "nav.feedback": "Feedback",
    "nav.about": "Despre",
  },
  el: {
    "nav.weekend": "Σαββατοκύριακο",
    "nav.standings": "Κατάταξη",
    "nav.drivers": "Οδηγοί",
    "nav.feedback": "Σχόλια",
    "nav.about": "Σχετικά",
  },
  sv: {
    "nav.weekend": "Helg",
    "nav.standings": "Ställning",
    "nav.drivers": "Förare",
    "nav.feedback": "Feedback",
    "nav.about": "Om",
  },
  da: {
    "nav.weekend": "Weekend",
    "nav.standings": "Stilling",
    "nav.drivers": "Kørere",
    "nav.feedback": "Feedback",
    "nav.about": "Om",
  },
  fi: {
    "nav.weekend": "Viikonloppu",
    "nav.standings": "Sijoitukset",
    "nav.drivers": "Kuljettajat",
    "nav.feedback": "Palaute",
    "nav.about": "Tietoa",
  },
  no: {
    "nav.weekend": "Helg",
    "nav.standings": "Mesterskap",
    "nav.drivers": "Førere",
    "nav.feedback": "Tilbakemelding",
    "nav.about": "Om",
  },
  is: {
    "nav.weekend": "Helgi",
    "nav.standings": "Staða",
    "nav.drivers": "Ökumenn",
    "nav.feedback": "Endurgjöf",
    "nav.about": "Um",
  },
  sk: {
    "nav.weekend": "Víkend",
    "nav.standings": "Poradie",
    "nav.drivers": "Jazdci",
    "nav.feedback": "Spätná väzba",
    "nav.about": "O nás",
  },
  sl: {
    "nav.weekend": "Vikend",
    "nav.standings": "Razvrstitev",
    "nav.drivers": "Dirkači",
    "nav.feedback": "Povratne informacije",
    "nav.about": "O nas",
  },
  hr: {
    "nav.weekend": "Vikend",
    "nav.standings": "Poredak",
    "nav.drivers": "Vozači",
    "nav.feedback": "Povratne informacije",
    "nav.about": "O nama",
  },
  bg: {
    "nav.weekend": "Уикенд",
    "nav.standings": "Класиране",
    "nav.drivers": "Пилоти",
    "nav.feedback": "Обратна връзка",
    "nav.about": "За нас",
  },
  et: {
    "nav.weekend": "Nädalavahetus",
    "nav.standings": "Edetabel",
    "nav.drivers": "Sõitjad",
    "nav.feedback": "Tagasiside",
    "nav.about": "Meist",
  },
  lv: {
    "nav.weekend": "Nedēļas nogale",
    "nav.standings": "Kopvērtējums",
    "nav.drivers": "Piloti",
    "nav.feedback": "Atsauksmes",
    "nav.about": "Par",
  },
  lt: {
    "nav.weekend": "Savaitgalis",
    "nav.standings": "Įskaita",
    "nav.drivers": "Lenktynininkai",
    "nav.feedback": "Atsiliepimai",
    "nav.about": "Apie",
  },
  ga: {
    "nav.weekend": "Deireadh seachtaine",
    "nav.standings": "Seasamh",
    "nav.drivers": "Tiománaithe",
    "nav.feedback": "Aiseolas",
    "nav.about": "Fúinn",
  },
  mt: {
    "nav.weekend": "Tmiem il-ġimgħa",
    "nav.standings": "Klassifika",
    "nav.drivers": "Sewwieqa",
    "nav.feedback": "Feedback",
    "nav.about": "Dwarna",
  },
  uk: {
    "nav.weekend": "Вікенд",
    "nav.standings": "Залік",
    "nav.drivers": "Пілоти",
    "nav.feedback": "Відгук",
    "nav.about": "Про нас",
  },
  sr: {
    "nav.weekend": "Vikend",
    "nav.standings": "Poredak",
    "nav.drivers": "Vozači",
    "nav.feedback": "Povratne informacije",
    "nav.about": "O nama",
  },
  sq: {
    "nav.weekend": "Fundjavë",
    "nav.standings": "Renditja",
    "nav.drivers": "Pilotët",
    "nav.feedback": "Komente",
    "nav.about": "Rreth",
  },
  mk: {
    "nav.weekend": "Викенд",
    "nav.standings": "Пласман",
    "nav.drivers": "Возачи",
    "nav.feedback": "Повратни информации",
    "nav.about": "За нас",
  },
  bs: {
    "nav.weekend": "Vikend",
    "nav.standings": "Poredak",
    "nav.drivers": "Vozači",
    "nav.feedback": "Povratne informacije",
    "nav.about": "O nama",
  },
  ru: {
    "nav.weekend": "Уикенд",
    "nav.standings": "Зачёт",
    "nav.drivers": "Пилоты",
    "nav.feedback": "Обратная связь",
    "nav.about": "О нас",
  },
  be: {
    "nav.weekend": "Вікэнд",
    "nav.standings": "Залік",
    "nav.drivers": "Пілоты",
    "nav.feedback": "Зваротная сувязь",
    "nav.about": "Пра нас",
  },
  lb: {
    "nav.weekend": "Weekend",
    "nav.standings": "Klassement",
    "nav.drivers": "Piloten",
    "nav.feedback": "Feedback",
    "nav.about": "Iwwer eis",
  },
  ka: {
    "nav.weekend": "უიქენდი",
    "nav.standings": "შედეგები",
    "nav.drivers": "პილოტები",
    "nav.feedback": "უკუკავშირი",
    "nav.about": "შესახებ",
  },
  hy: {
    "nav.weekend": "Շաբաթավերջ",
    "nav.standings": "Դասակարգում",
    "nav.drivers": "Վարորդներ",
    "nav.feedback": "Հետադարձ կապ",
    "nav.about": "Մեր մասին",
  },
  ko: {
    "nav.weekend": "레이스 주말",
    "nav.standings": "순위",
    "nav.drivers": "드라이버",
    "nav.feedback": "피드백",
    "nav.about": "정보",
  },
  he: {
    "nav.weekend": "סוף שבוע",
    "nav.standings": "דירוג",
    "nav.drivers": "נהגים",
    "nav.feedback": "משוב",
    "nav.about": "אודות",
  },
  id: {
    "nav.weekend": "Akhir pekan",
    "nav.standings": "Klasemen",
    "nav.drivers": "Pembalap",
    "nav.feedback": "Masukan",
    "nav.about": "Tentang",
  },
  ms: {
    "nav.weekend": "Hujung minggu",
    "nav.standings": "Kedudukan",
    "nav.drivers": "Pemandu",
    "nav.feedback": "Maklum balas",
    "nav.about": "Tentang",
  },
};

interface I18nValue {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: Translate;
}

const I18nContext = createContext<I18nValue | null>(null);
const storageKey = "ps-language";

function isLanguageCode(value: string): value is LanguageCode {
  return supportedLanguages.some(([code]) => code === value);
}

function initialLanguage(): LanguageCode {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(storageKey);
  } catch {
    saved = null;
  }
  if (saved !== null && isLanguageCode(saved)) return saved;
  const browser = navigator.language.split("-")[0];
  return isLanguageCode(browser) ? browser : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(initialLanguage);
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir =
      language === "ar" || language === "he" ? "rtl" : "ltr";
  }, [language]);
  const setLanguage = (next: LanguageCode) => {
    setLanguageState(next);
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      return;
    }
  };
  const t = useMemo<Translate>(() => {
    const selected = translated[language] ?? {};
    return (key, values) => {
      let result = selected[key] ?? english[key];
      for (const [name, value] of Object.entries(values ?? {})) {
        result = result.replaceAll(`{${name}}`, String(value));
      }
      return result;
    };
  }, [language]);
  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null)
    throw new Error("useI18n must be used inside I18nProvider.");
  return value;
}
